import { BaseSyntheticEvent } from "react";
import { Drawer } from "@material-tailwind/react";
import Button from "../../../components/Button";
import Typography from "../../../components/Typography";
import ActionIcon from "../../../components/ActionIcon";
import {
  changeFilterAction,
  changePaginationAction,
  loadProjectsSpecific,
  resetFilterAction,
} from "../../../store/projects/actions";
import { dispatch } from "../../../store";
import MultiSelectOption from "../../../components/MultiSelect/SelectOption";
import { MultiSelect } from "../../../components/MultiSelect";
import SelectLocationInput from "./SelectLocationInput";

import {
  useCarbonProjectsSelector,
  useFilterProjectSelector,
  useFilterValuesSelector,
  useRecommendedProjectsSelector,
  useSelectedProjectSelector,
} from "../../../store/projects/selectors";
import DatePicker from "../../../components/DatePicker";
import MinMaxSlider from "../../../components/MinMaxSlider";
import Input from "../../../components/Input";

interface DrawerDefaultProps {
  isOpen: boolean;
  onClose: () => void;
}

//TODO: find way to make drawer smooth with overlay
export function FilterDrawer({ isOpen, onClose }: DrawerDefaultProps) {
  const selected = useSelectedProjectSelector();
  const filter = useFilterProjectSelector();
  const {
    methodology,
    type_id: typeId,
    start_date: startDate,
    end_date: endDate,
    min_estimated_annual_emission_reduction: minAnnualCredits,
    max_estimated_annual_emission_reduction: maxAnnualCredits,
  } = filter;

  const handleFilterChange = (key: string, value: unknown) => {
    dispatch(changeFilterAction({ [key]: value }));
  };

  const filterValuesState = useFilterValuesSelector();
  const methodologies = filterValuesState?.data?.methodologies || [];
  const types = filterValuesState?.data?.types || [];
  const max_estimated_annual_emission_reduction =
    filterValuesState?.data?.max_estimated_annual_emission_reduction || 999999999;

  const {
    // data: {
    //   items: carbonProjects,
    //   pagination: { total: carbonTotal },
    // },
    isLoading: isLoadingCarbon,
    // error: carbonError,
  } = useCarbonProjectsSelector();

  const {
    isLoading: isLoadingRecommended,
    // error: recommendedError,
  } = useRecommendedProjectsSelector();

  const handleApplyFilters = () => {
    dispatch(changePaginationAction({ page: 1 }));
    dispatch(loadProjectsSpecific.submit());
  };

  const handleResetFilters = () => {
    dispatch(resetFilterAction());
    handleApplyFilters();
  };

  const rangeFilter = {
    min: 0,
    max: max_estimated_annual_emission_reduction,
    gap: 0,
  };

  const closeIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className="h-5 w-5"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  const handleMinChange = (e: BaseSyntheticEvent) => {
    handleFilterChange(
      "min_estimated_annual_emission_reduction",
      Number(
        Number(e.target.value) < Number(maxAnnualCredits) - rangeFilter.gap
          ? e.target.value
          : Number(maxAnnualCredits) - rangeFilter.gap,
      ).toFixed(0),
    );
  };

  const handleMaxChange = (e: BaseSyntheticEvent) => {
    handleFilterChange(
      "max_estimated_annual_emission_reduction",
      Number(
        Number(e.target.value) > Number(minAnnualCredits) + rangeFilter.gap
          ? e.target.value
          : Number(minAnnualCredits) + rangeFilter.gap,
      ).toFixed(0),
    );
  };

  return (
    <Drawer
      placement="right"
      placeholder="filter"
      open={isOpen}
      onClose={onClose}
      className="z-10 mt-14 border-r border-gray-500 p-6 shadow-lg"
      size={480}
      transition={{ type: "tween", duration: 0.5 }}
      overlay={false}
    >
      <div className="mb-6 flex items-center justify-between">
        <Typography variant="h5">Filters</Typography>
        <ActionIcon icon={closeIcon} variant="text" color="gray" onClick={onClose} />
      </div>
      {selected === "current" ? (
        <div className="flex flex-col gap-6">
          <SelectLocationInput className="h-full w-full" />
          <MultiSelect
            className="h-full w-full"
            label="Type"
            color="gray"
            variant="outlined"
            value={typeId}
            onChange={(value: any) => {
              const copy: any = new Set(typeId);

              const isExist = copy.has(value);

              if (isExist) {
                copy.delete(value);
                handleFilterChange("type_id", [...copy]);
              } else {
                copy.add(value);
                handleFilterChange("type_id", [...copy]);
              }
            }}
          >
            {types?.slice(0, 128).map((item: any, index) => (
              <MultiSelectOption value={item.id} key={`${index}-${item.name}`}>
                {item.name}
              </MultiSelectOption>
            ))}
          </MultiSelect>
          <MultiSelect
            className="h-full w-full"
            label="Methodology"
            color="gray"
            variant="outlined"
            value={methodology}
            onChange={(value: any) => {
              const copy: any = new Set(methodology);

              const isExist = copy.has(value);

              if (isExist) {
                copy.delete(value);
                handleFilterChange("methodology", [...copy]);
              } else {
                copy.add(value);
                handleFilterChange("methodology", [...copy]);
              }
            }}
          >
            {methodologies?.slice(0, 128).map((item: any, index) => (
              <MultiSelectOption value={item} key={`${index}`}>
                {item}
              </MultiSelectOption>
            ))}
          </MultiSelect>
          <div className="flex flex-row gap-6">
            <DatePicker
              label="Select start date"
              value={startDate}
              onChange={(value: any) => {
                if (value.constructor.name === "SyntheticBaseEvent")
                  handleFilterChange("start_date", null);
                else handleFilterChange("start_date", new Date(value).toISOString().split("T")[0]);
              }}
            />
            <DatePicker
              label="Select end date"
              value={endDate}
              onChange={(value: any) => {
                if (value.constructor.name === "SyntheticBaseEvent")
                  handleFilterChange("end_date", null);
                else handleFilterChange("end_date", new Date(value).toISOString().split("T")[0]);
              }}
            />
          </div>
          <div>
            <Typography variant="small" color="black" className="mt-0 pb-4">
              Estimated annual credits:
            </Typography>
            <div className="mb-4 grid grid-cols-2 gap-6">
              <Input label="From" value={minAnnualCredits} onChange={(e) => handleMinChange(e)} />
              <Input label="To" value={maxAnnualCredits} onChange={(e) => handleMaxChange(e)} />
            </div>
            <MinMaxSlider
              max={rangeFilter.max}
              minValue={minAnnualCredits}
              maxValue={maxAnnualCredits}
              onMinChange={(e) => handleMinChange(e)}
              onMaxChange={(e) => handleMaxChange(e)}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <SelectLocationInput className="h-full w-full" />
          <MultiSelect
            className="h-full w-full"
            label="Methodology"
            color="gray"
            variant="outlined"
            value={methodology}
            onChange={(value: any) => {
              const copy: any = new Set(methodology);

              const isExist = copy.has(value);

              if (isExist) {
                copy.delete(value);
                handleFilterChange("methodology", [...copy]);
              } else {
                copy.add(value);
                handleFilterChange("methodology", [...copy]);
              }
            }}
          >
            {methodologies?.slice(0, 128).map((item: any, index) => (
              <MultiSelectOption value={item} key={`${index}`}>
                {item}
              </MultiSelectOption>
            ))}
          </MultiSelect>
        </div>
      )}
      <div className="fixed bottom-6 flex flex-row justify-end gap-[10px] pr-2">
        <Button size="md" variant="outlined" onClick={handleResetFilters}>
          Reset Filter
        </Button>
        <Button
          size="md"
          color="gray"
          loading={isLoadingCarbon || isLoadingRecommended}
          onClick={handleApplyFilters}
        >
          Apply Filter
        </Button>
      </div>
    </Drawer>
  );
}
